import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dropzone } from '@/components/ui/dropzone';
import { AttachmentsList } from '@/features/attachments';
import DriveFolderSection from './DriveFolderSection';
import { uploadAccountDocument } from '../api';

interface DocumentsTabProps {
  accountId: string;
}

export default function DocumentsTab({ accountId }: DocumentsTabProps) {
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const uploadFiles = async (files: File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setUploading(true);
    let okCount = 0;
    let failCount = 0;
    for (const file of arr) {
      try {
        await uploadAccountDocument(accountId, file);
        okCount += 1;
      } catch (err) {
        failCount += 1;
        const msg = err instanceof Error ? err.message : 'Upload failed';
        toast.error(`${file.name}: ${msg}`);
      }
    }
    setUploading(false);
    if (okCount > 0) {
      toast.success(
        okCount === 1
          ? `Uploaded to Drive + DMS (OCR queued)`
          : `Uploaded ${okCount} files to Drive + DMS (OCR queued)`,
      );
      queryClient.invalidateQueries({ queryKey: ['account-drive-files', accountId] });
      queryClient.invalidateQueries({ queryKey: ['attachments', 'account', accountId] });
    }
    if (failCount > 0 && okCount === 0) {
      // already toasted each failure above
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Looking for a technician intervention report?{' '}
        <Link className="text-primary underline" to={`/accounts/${accountId}?tab=interventions`}>
          Open Interventions
        </Link>
      </p>
      <Dropzone onFiles={uploadFiles} disabled={uploading} className="p-8">
        {uploading ? (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Uploading to Drive + DMS…</p>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drop files here to upload to Google Drive AND DMS (with OCR), or click to choose.
            </p>
          </>
        )}
      </Dropzone>

      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>
            DMS documents attached to this account. Link existing documents or open them in the DMS.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AttachmentsList
            entityType="account"
            entityId={accountId}
            emptyText="No documents attached to this account yet."
          />
        </CardContent>
      </Card>

      <DriveFolderSection accountId={accountId} />
    </div>
  );
}
